/**
 * Knowledge Graph API Integration
 * Connects Varahi MHI to the Knowledge Graph RAG system
 */

const KG_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Query the knowledge graph with a natural language question
 * Now uses HYBRID RAG endpoint (vector + BM25 + knowledge graph)
 * @param {string} question - User's question
 * @param {string} vehicleType - Vehicle type ('nexon' | 'nano' | 'compressor')
 * @param {boolean} includeContext - Whether to include multi-hop traversal
 * @param {Array} conversationHistory - Last 15 messages for context (optional)
 * @param {boolean} useHybrid - Whether to use hybrid endpoint (default: true)
 * @returns {Promise<Object>} - Response with answer, reasoning path, and retrieval details
 */
export const queryKnowledgeGraph = async (question, vehicleType = 'nexon', includeContext = false, conversationHistory = [], useHybrid = true) => {
  try {
    // Use unified query endpoint that handles vehicle routing
    const endpoint = 'query';

    console.log('[KG API] Starting query...');
    console.log('[KG API] URL:', `${KG_API_BASE_URL}/${endpoint}`);
    console.log('[KG API] Question:', question);
    console.log('[KG API] Vehicle:', vehicleType);
    console.log('[KG API] Using Hybrid RAG:', useHybrid);
    console.log('[KG API] Conversation History:', conversationHistory.length, 'messages');

    const response = await fetch(`${KG_API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question: question,
        vehicle_type: vehicleType,
        include_context: includeContext,
        use_hybrid: useHybrid,
        conversation_history: conversationHistory,
      }),
    });

    console.log('[KG API] Response status:', response.status);
    console.log('[KG API] Response OK:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[KG API] Error response:', errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('[KG API] Response data:', data);

    // NEW: Extract visual content (images and tables)
    const visualContent = data.visual_content || {};
    const images = visualContent.images || [];
    const tables = visualContent.tables || [];

    console.log('[KG API] 🎨 VISUAL CONTENT DETECTED:');
    console.log('[KG API] 📷 Images found:', images.length);
    console.log('[KG API] 📊 Tables found:', tables.length);

    if (images.length > 0) {
      console.log('[KG API] 🖼️ Image details:');
      images.forEach((img, idx) => {
        console.log(`[KG API]   Image ${idx + 1}:`, {
          page: img.page,
          file_path: img.file_path,
          description: img.description,
          section: img.section
        });
      });
    }

    if (tables.length > 0) {
      console.log('[KG API] 📋 Table details:');
      tables.forEach((table, idx) => {
        console.log(`[KG API]   Table ${idx + 1}:`, {
          page: table.page,
          caption: table.caption,
          rows: table.dimensions?.rows,
          cols: table.dimensions?.cols
        });
      });
    }

    return {
      success: true,
      answer: data.answer,
      intent: data.intent,
      confidence: data.confidence,
      citations: data.citations,
      reasoningPath: data.reasoning_path || [],
      // Hybrid RAG retrieval details
      retrievalDetails: data.retrieval_details || null,
      isHybrid: useHybrid && data.intent === 'hybrid',
      // NEW: Vehicle-specific information
      vehicleType: data.vehicle_type || vehicleType,
      vehicleDescription: data.vehicle_description || '',
      retrievalMethod: data.retrieval_method || '',
      // NEW: Multimodal content
      images: images,
      tables: tables,
      hasVisualContent: images.length > 0 || tables.length > 0,
    };
  } catch (error) {
    console.error('[KG API] Knowledge graph query failed:', error);
    console.error('[KG API] Error type:', error.constructor.name);
    console.error('[KG API] Error message:', error.message);
    return {
      success: false,
      error: error.message,
      answer: null,
      reasoningPath: [],
      retrievalDetails: null,
    };
  }
};

/**
 * Get available vehicle systems
 * @returns {Promise<Object>} - List of available vehicles
 */
export const getAvailableVehicles = async () => {
  try {
    const response = await fetch(`${KG_API_BASE_URL}/vehicles`);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      vehicles: data.vehicles || [],
      default: data.default || 'nexon',
    };
  } catch (error) {
    console.error('Failed to fetch available vehicles:', error);
    return {
      success: false,
      error: error.message,
      vehicles: [],
    };
  }
};

/**
 * Get knowledge graph statistics
 * @returns {Promise<Object>} - Graph statistics
 */
export const getGraphStats = async () => {
  try {
    const response = await fetch(`${KG_API_BASE_URL}/stats`);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      stats: data,
    };
  } catch (error) {
    console.error('Failed to fetch graph stats:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Check API health
 * @returns {Promise<Object>} - Health status
 */
export const checkAPIHealth = async () => {
  try {
    const response = await fetch(`${KG_API_BASE_URL}/health`);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      status: data.status,
      database: data.database,
    };
  } catch (error) {
    console.error('Health check failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Format citations for display
 * @param {Array} citations - Array of citation objects
 * @returns {string} - Formatted citation text
 */
export const formatCitations = (citations) => {
  if (!citations || citations.length === 0) {
    return '';
  }

  const pages = citations.map(c => c.page).filter(Boolean);
  const uniquePages = [...new Set(pages)].sort((a, b) => a - b);

  if (uniquePages.length === 0) {
    return '';
  }

  return `(Reference: Page${uniquePages.length > 1 ? 's' : ''} ${uniquePages.join(', ')})`;
};

/**
 * Determine if query should use knowledge graph
 * @param {string} query - User query
 * @returns {boolean} - Whether to use KG
 */
export const shouldUseKnowledgeGraph = (query) => {
  // Keywords that indicate owner's manual queries
  const kgKeywords = [
    // Question words
    'what', 'how', 'where', 'when', 'why', 'tell', 'show', 'find', 'list',

    // Troubleshooting & Problems
    'unable', 'can\'t', 'cannot', 'won\'t', 'not working', 'broken', 'failed',
    'problem', 'issue', 'error', 'trouble', 'troubleshoot', 'help', 'fix',
    'repair', 'diagnose', 'diagnostic', 'fault', 'malfunction', 'doesn\'t',
    'start', 'starting', 'won\'t start', 'engine', 'crank', 'turn on',

    // Components & Systems
    'battery', 'charge', 'motor', 'power', 'range', 'component', 'system',
    'airbag', 'brake', 'instrument', 'cluster', 'charging', 'location',
    'port', 'headlamp', 'indicator', 'high voltage', 'wheel', 'tire',
    'seat', 'door', 'window', 'mirror', 'suspension', 'steering',
    'fuel', 'petrol', 'ignition', 'starter', 'alternator', 'transmission',

    // Manual-related terms
    'specification', 'manual', 'warning', 'procedure', 'information',
    'capacity', 'voltage', 'current', 'speed', 'detail', 'about',
    'maintenance', 'service', 'safety', 'protocol', 'guideline',

    // Technical terms
    'kWh', 'volt', 'amp', 'watt', 'km', 'mph', 'temperature',

    // Actions
    'operate', 'use', 'activate', 'check', 'inspect', 'replace'
  ];

  const lowerQuery = query.toLowerCase();

  // Check if query contains any KG keywords
  const hasKeyword = kgKeywords.some(keyword => lowerQuery.includes(keyword));

  // Also route to KG if query is short and likely a command/topic
  const isShortQuery = query.trim().split(' ').length <= 4;

  return hasKeyword || isShortQuery;
};

/**
 * Export conversation to PDF with images and tables
 * @param {string} query - User's question
 * @param {Object} response - AI response with visual_content
 * @param {string} vehicleName - Vehicle name for report
 * @returns {Promise<Blob>} - PDF file blob
 */
export const exportToPDF = async (query, response, vehicleName = 'Vehicle') => {
  try {
    console.log('[KG API] Exporting to PDF...');
    console.log('[KG API] Query:', query);
    console.log('[KG API] Vehicle:', vehicleName);

    const apiResponse = await fetch(`${KG_API_BASE_URL}/export-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: query,
        response: response,
        vehicle_name: vehicleName,
      }),
    });

    if (!apiResponse.ok) {
      const errorText = await apiResponse.text();
      console.error('[KG API] PDF export error:', errorText);
      throw new Error(`PDF export failed: ${apiResponse.status}`);
    }

    const blob = await apiResponse.blob();
    console.log('[KG API] PDF generated successfully');

    return blob;
  } catch (error) {
    console.error('[KG API] PDF export failed:', error);
    throw error;
  }
};

/**
 * Export full conversation to PDF with all messages, images, and tables
 * @param {Array} conversationHistory - Complete conversation history
 * @param {string} vehicleName - Vehicle name for report
 * @returns {Promise<Blob>} - PDF file blob
 */
export const exportConversationToPDF = async (conversationHistory, vehicleName = 'Vehicle') => {
  try {
    console.log('[KG API] Exporting full conversation to PDF...');
    console.log('[KG API] Messages:', conversationHistory.length);
    console.log('[KG API] Vehicle:', vehicleName);

    const apiResponse = await fetch(`${KG_API_BASE_URL}/export-conversation-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        conversation_history: conversationHistory,
        vehicle_name: vehicleName,
      }),
    });

    if (!apiResponse.ok) {
      const errorText = await apiResponse.text();
      console.error('[KG API] Conversation PDF export error:', errorText);
      throw new Error(`Conversation PDF export failed: ${apiResponse.status}`);
    }

    const blob = await apiResponse.blob();
    console.log('[KG API] Conversation PDF generated successfully');

    return blob;
  } catch (error) {
    console.error('[KG API] Conversation PDF export failed:', error);
    throw error;
  }
};

export default {
  queryKnowledgeGraph,
  getAvailableVehicles,
  getGraphStats,
  checkAPIHealth,
  formatCitations,
  shouldUseKnowledgeGraph,
  exportToPDF,  // Single message PDF export
  exportConversationToPDF,  // NEW: Full conversation PDF export
};
